import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["item", "dropzone"]
  static values = {
    url: String,
    addUrl: String
  }

  connect() {
    console.log("Sortable controller connected")
    console.log("URL value:", this.urlValue)
    console.log("Add URL value:", this.addUrlValue)
    this.draggedElement = null
  }

  dragStart(event) {
    this.draggedElement = event.currentTarget
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/html", event.currentTarget.innerHTML)
    event.currentTarget.style.opacity = "0.5"
  }

  dragEnd(event) {
    event.currentTarget.style.opacity = "1"

    // Remove all drag-over classes
    this.itemTargets.forEach(item => {
      item.classList.remove("drag-over")
    })
  }

  dragOver(event) {
    if (event.preventDefault) {
      event.preventDefault()
    }

    event.dataTransfer.dropEffect = "move"

    const afterElement = this.getDragAfterElement(event.clientY)
    const draggable = this.draggedElement

    if (afterElement == null) {
      this.element.appendChild(draggable)
    } else {
      this.element.insertBefore(draggable, afterElement)
    }

    return false
  }

  drop(event) {
    if (event.stopPropagation) {
      event.stopPropagation()
    }

    this.saveNewOrder()

    return false
  }

  getDragAfterElement(y) {
    const draggableElements = [...this.itemTargets].filter(item => item !== this.draggedElement)

    return draggableElements.reduce((closest, child) => {
      const box = child.getBoundingClientRect()
      const offset = y - box.top - box.height / 2

      if (offset < 0 && offset > closest.offset) {
        return { offset: offset, element: child }
      } else {
        return closest
      }
    }, { offset: Number.NEGATIVE_INFINITY }).element
  }

  async saveNewOrder() {
    const positions = {}
    this.itemTargets.forEach((item, index) => {
      positions[item.dataset.id] = index
    })

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify({ positions })
      })

      if (!response.ok) {
        throw new Error("Failed to reorder elements")
      }

      console.log("Elements reordered successfully")
    } catch (error) {
      console.error("Error reordering elements:", error)
      alert("Failed to reorder elements. Please refresh and try again.")
    }
  }

  setupDragAndDrop() {
    this.element.addEventListener("dragenter", this.handleDragEnter.bind(this))
    this.element.addEventListener("dragover", this.handleDragOver.bind(this))
    this.element.addEventListener("dragleave", this.handleDragLeave.bind(this))
    this.element.addEventListener("drop", this.handleDrop.bind(this))
    this.element.addEventListener("dragend", this.handleDragEnd.bind(this))
  }

  handleDragEnter(event) {
    event.preventDefault()
    const dropzone = event.target.closest("[data-sortable-target='dropzone']")
    if (dropzone) {
      dropzone.classList.add("border-blue-500", "bg-blue-50")
    }
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
  }

  handleDragLeave(event) {
    const dropzone = event.target.closest("[data-sortable-target='dropzone']")
    if (dropzone && !dropzone.contains(event.relatedTarget)) {
      dropzone.classList.remove("border-blue-500", "bg-blue-50")
    }
  }

  handleDrop(event) {
    event.preventDefault()
    console.log("Drop event triggered")

    const dropzone = event.target.closest("[data-sortable-target='dropzone']")
    if (dropzone) {
      dropzone.classList.remove("border-blue-500", "bg-blue-50")
    }

    // Try to get the data in different formats
    let elementId = event.dataTransfer.getData("text/plain")
    console.log("Plain text data:", elementId)

    if (!elementId) {
      try {
        const jsonData = JSON.parse(event.dataTransfer.getData("application/json"))
        elementId = jsonData.id
        console.log("JSON data:", jsonData)
      } catch (e) {
        console.error("Failed to parse JSON data:", e)
      }
    }

    if (!elementId) {
      console.error("No element ID found in drop data")
      return
    }

    // If dropping on the dropzone, add a new element
    if (dropzone) {
      console.log("Adding new element:", elementId)
      this.addElement(elementId)
    } else {
      // Otherwise, handle reordering
      const targetItem = event.target.closest("[data-sortable-target='item']")
      if (targetItem) {
        const targetPosition = Array.from(this.itemTargets).indexOf(targetItem)
        this.reorderElement(elementId, targetPosition)
      }
    }
  }

  handleDragEnd(event) {
    event.preventDefault()
    const dropzone = this.dropzoneTarget
    if (dropzone) {
      dropzone.classList.remove("border-blue-500", "bg-blue-50")
    }
  }

  async addElement(elementId) {
    console.log("Adding element:", elementId)
    console.log("Add URL:", this.addUrlValue)

    if (!this.addUrlValue) {
      console.error("No add URL configured")
      return
    }

    try {
      const csrfToken = document.querySelector("[name='csrf-token']")?.content
      if (!csrfToken) {
        console.error("No CSRF token found")
        return
      }

      console.log("Making request to:", this.addUrlValue)
      const response = await fetch(this.addUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          element_id: elementId,
          position: this.itemTargets.length
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to add element: ${errorText}`)
      }

      console.log("Element added successfully")
      window.location.reload()
    } catch (error) {
      console.error("Error adding element:", error)
    }
  }

  async reorderElement(elementId, targetPosition) {
    console.log("Reordering element:", elementId, "to position:", targetPosition)

    const positions = {}
    this.itemTargets.forEach((item, index) => {
      positions[item.dataset.id] = index
    })
    positions[elementId] = targetPosition

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify({ positions })
      })

      if (!response.ok) {
        throw new Error("Failed to reorder elements")
      }

      console.log("Elements reordered successfully")
      window.location.reload()
    } catch (error) {
      console.error("Error reordering elements:", error)
    }
  }
}